import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Row,
  Segmented,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSiteAssetStatus,
  type SiteAssetStatus,
} from '~/app/hooks/site/useSiteAssetMap';
import { SiteAssetsMap } from '~/app/components/site-asset-map/SiteAssetsMap';
import type { SiteAssetMapPageViewProps } from './SiteAssetMapPage.types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { FilterBarRefreshButton } from '~/app/components/common/FilterBar/FilterBarToolbar';

const { Paragraph, Text, Title } = Typography;

function getStatusColor(status: SiteAssetStatus): string {
  if (status === 'healthy') {
    return 'green';
  }

  if (status === 'warning') {
    return 'orange';
  }

  return 'red';
}

export function SiteAssetMapPageView({
  assets,
  columns,
  error,
  isFetching,
  isLoading,
  mapMode,
  onBack,
  onMapModeChange,
  onRefresh,
  site,
  statusCounts,
}: SiteAssetMapPageViewProps) {
  const { t } = useTranslation('sites');

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Flex justify="space-between" align="flex-start" gap={16} wrap>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            style={{ marginBottom: 12 }}
            onClick={onBack}
          >
            {t('detail.backToSites', 'Back to sites')}
          </Button>
          <Title level={2} style={{ marginBottom: 8 }}>
            {site?.name ?? t('detail.titleFallback', 'Site details')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 4 }}>
            {t(
              'detail.description',
              'Monitor the assets installed on this site and their current open-incident pressure.',
            )}
          </Paragraph>
          {site?.address ? <Text type="secondary">{site.address}</Text> : null}
        </div>

        <FilterBarRefreshButton
          ns="sites"
          labelKey="actions.refresh"
          loading={isFetching}
          onClick={onRefresh}
        />
      </Flex>

      {error ? (
        <Card style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}>
          <Alert
            type="error"
            showIcon
            message={t('detail.loadFailed', 'Failed to load site asset map.')}
            description={error.message}
          />
        </Card>
      ) : null}

      <Card style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 8 }}>
              {t('detail.map.title', 'Asset map')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {mapMode === 'focused'
                ? t(
                    'detail.map.focusedDescription',
                    'Focused mode mutes roads and nearby labels so the focused sites stand out clearly.',
                  )
                : t(
                    'detail.map.standardDescription',
                    'Standard mode keeps the regular map context while preserving the status pin colors.',
                  )}
            </Paragraph>
          </div>

          <Segmented<'focused' | 'standard'>
            block
            options={[
              {
                label: t('detail.map.modes.focused', 'Focused'),
                value: 'focused',
              },
              {
                label: t('detail.map.modes.standard', 'Standard'),
                value: 'standard',
              },
            ]}
            value={mapMode}
            onChange={onMapModeChange}
          />

          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} lg={16}>
              <SiteAssetsMap
                assets={assets}
                latitude={site?.latitude ?? null}
                longitude={site?.longitude ?? null}
                perimeter={site?.perimeter ?? null}
                siteName={site?.name ?? ''}
                mode={mapMode}
                emptyText={t(
                  'detail.map.empty',
                  'No assets are assigned to this site yet.',
                )}
                unavailableText={t(
                  'detail.map.unavailable',
                  'This site has no coordinates yet, so the asset map cannot be displayed.',
                )}
                missingKeyText={t(
                  'detail.map.missingKey',
                  'Google Maps is not configured for this environment.',
                )}
              />
            </Col>

            <Col xs={24} lg={8}>
              <Flex vertical gap={12} style={{ height: '100%' }}>
                <div
                  style={{
                    border: '1px solid #dcfce7',
                    borderRadius: 14,
                    padding: 16,
                    background: '#f0fdf4',
                  }}
                >
                  <Statistic
                    title={t('detail.summary.healthy', 'Healthy assets')}
                    value={statusCounts.healthy}
                    valueStyle={{ color: '#16a34a' }}
                  />
                </div>

                <div
                  style={{
                    border: '1px solid #fde68a',
                    borderRadius: 14,
                    padding: 16,
                    background: '#fffbeb',
                  }}
                >
                  <Statistic
                    title={t('detail.summary.warning', 'Half affected assets')}
                    value={statusCounts.warning}
                    valueStyle={{ color: '#f59e0b' }}
                  />
                </div>

                <div
                  style={{
                    border: '1px solid #fecaca',
                    borderRadius: 14,
                    padding: 16,
                    background: '#fef2f2',
                  }}
                >
                  <Statistic
                    title={t('detail.summary.critical', 'Critical assets')}
                    value={statusCounts.critical}
                    valueStyle={{ color: '#dc2626' }}
                  />
                </div>
              </Flex>
            </Col>
          </Row>

          <Text type="secondary">
            {mapMode === 'focused'
              ? t(
                  'detail.map.focusedHint',
                  'Focused mode reduces visual noise and keeps the colored pins as the main reference on the map.',
                )
              : t(
                  'detail.map.offsetHint',
                  'When individual asset coordinates are unavailable, markers are slightly spread around the site position to keep them readable.',
                )}
          </Text>
        </Space>
      </Card>

      <Card
        style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}
        styles={{ body: { padding: 0 } }}
      >
        <EvimoTable
          className="ds-table"
          columns={columns.map((column) => {
            if (column.key !== 'status') {
              return column;
            }

            return {
              ...column,
              render: (_value: unknown, asset: (typeof assets)[number]) => {
                const status = getSiteAssetStatus(asset.openIncidentsCount);

                return (
                  <Tag color={getStatusColor(status)}>
                    {t(`detail.status.${status}`, status)}
                  </Tag>
                );
              },
            };
          })}
          dataSource={assets}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 920 }}
          locale={{
            emptyText: t(
              'detail.table.empty',
              'No assets are currently linked to this site.',
            ),
          }}
        />
      </Card>
    </Space>
  );
}
